import moment from 'moment';
import {
  OrderStatusName,
  type OrderGuestStatuses,
} from 'plugins/CheckoutPlugin/types/CheckoutTypes';
import { type ExperienceGuestOrderStatusMessageType } from 'plugins/LocationPlugin/types/LocationTypes';

export type OrderStatusNameUI =
  | 'ongoing'
  | 'completed'
  | 'cancelled'
  | 'pending';

export type OrderStatusTitleUI =
  | 'received'
  | 'preparing'
  | 'ready'
  | 'cancelled';

export type OrderStatusType = {
  id: number;
  title: OrderStatusTitleUI;
  titleSecondary?: boolean;
  status: OrderStatusNameUI | null;
  subtitle?: string;
  description?: string;
  descriptionSecondary?: string;
};

export const OrderStatusNameUIMapper = (name: OrderGuestStatuses['name']) => {
  switch (name) {
    case OrderStatusName.PREPARING:
    case OrderStatusName.PREPARING_NOT_PAID: {
      return 'preparing';
    }

    case OrderStatusName.READY_NOT_PAID:
    case OrderStatusName.READY: {
      return 'ready';
    }

    case OrderStatusName.CANCELLED: {
      return 'cancelled';
    }

    case OrderStatusName.RECEIVED:
    default: {
      return 'received';
    }
  }
};

const DateFormatter = (date: string, locationTimezone: string) =>
  moment.utc(date).tz(locationTimezone).format('ddd, MMM D, h:mm A').toString();

const updateDestinatedStatus = (
  locationTimezone: string,
  destinatedStatus: OrderStatusType,
  status: OrderStatusType['status'],
  date: OrderGuestStatuses['dateTime'],
  description:
    | ExperienceGuestOrderStatusMessageType[keyof ExperienceGuestOrderStatusMessageType]
    | null = null,
  name?: OrderGuestStatuses['name'],
  titleSecondary: boolean = false,
  descriptionSecondary:
    | ExperienceGuestOrderStatusMessageType[keyof ExperienceGuestOrderStatusMessageType]
    | null = null,
) => {
  destinatedStatus.status = status;
  destinatedStatus.subtitle = DateFormatter(date?.date, locationTimezone);
  destinatedStatus.titleSecondary = titleSecondary;

  if (name) {
    destinatedStatus.title = OrderStatusNameUIMapper(name);
  }

  if (description) {
    destinatedStatus.description = description;
  }

  if (descriptionSecondary) {
    destinatedStatus.descriptionSecondary = descriptionSecondary;
  }
};

export const FormattOrderStatus = (
  orderStatusesDate: OrderGuestStatuses[],
  orderStatusMessage: ExperienceGuestOrderStatusMessageType,
  locationTimezone: string,
  t: (key: string) => string,
): OrderStatusType[] => {
  if (!(orderStatusesDate.length || orderStatusMessage)) {
    return null;
  }

  const destinatedStatuses: OrderStatusType[] = [
    {
      id: 1,
      title: OrderStatusNameUIMapper(OrderStatusName.RECEIVED),
      status: 'ongoing' as const,
    },
    {
      id: 2,
      title: OrderStatusNameUIMapper(OrderStatusName.PREPARING),
      status: null,
    },
    {
      id: 3,
      title: OrderStatusNameUIMapper(OrderStatusName.READY),
      status: null,
    },
  ];

  const currentStatus = orderStatusesDate[orderStatusesDate.length - 1];

  switch (currentStatus.name) {
    case OrderStatusName.RECEIVED: {
      updateDestinatedStatus(
        locationTimezone,
        destinatedStatuses[0],
        'ongoing',
        currentStatus.dateTime,
        orderStatusMessage?.received ??
          t('orderStatus.statusMessages.received'),
      );

      return destinatedStatuses;
    }

    case OrderStatusName.PREPARING: {
      updateDestinatedStatus(
        locationTimezone,
        destinatedStatuses[0],
        'completed',
        orderStatusesDate[0]?.dateTime,
      );

      updateDestinatedStatus(
        locationTimezone,
        destinatedStatuses[1],
        'ongoing',
        orderStatusesDate[1]?.dateTime,
        orderStatusMessage?.preparing ??
          t('orderStatus.statusMessages.preparing'),
      );

      return destinatedStatuses;
    }

    case OrderStatusName.PREPARING_NOT_PAID: {
      updateDestinatedStatus(
        locationTimezone,
        destinatedStatuses[0],
        'completed',
        orderStatusesDate[0]?.dateTime,
      );

      updateDestinatedStatus(
        locationTimezone,
        destinatedStatuses[1],
        'pending',
        orderStatusesDate[1]?.dateTime,
        orderStatusMessage?.preparing_not_paid ??
          t('orderStatus.statusMessages.preparingNotPaid'),
        currentStatus.name,
        true,
        orderStatusMessage?.additional_preparing_not_paid ?? null,
      );

      return destinatedStatuses;
    }

    case OrderStatusName.READY: {
      updateDestinatedStatus(
        locationTimezone,
        destinatedStatuses[0],
        'completed',
        orderStatusesDate[0]?.dateTime,
      );

      updateDestinatedStatus(
        locationTimezone,
        destinatedStatuses[1],
        'completed',
        orderStatusesDate[1]?.dateTime,
      );

      updateDestinatedStatus(
        locationTimezone,
        destinatedStatuses[2],
        'completed',
        orderStatusesDate[2]?.dateTime,
        orderStatusMessage?.ready ?? t('orderStatus.statusMessages.ready'),
      );

      return destinatedStatuses;
    }

    case OrderStatusName.READY_NOT_PAID: {
      updateDestinatedStatus(
        locationTimezone,
        destinatedStatuses[0],
        'completed',
        orderStatusesDate[0]?.dateTime,
      );

      updateDestinatedStatus(
        locationTimezone,
        destinatedStatuses[1],
        'completed',
        orderStatusesDate[1]?.dateTime,
      );

      updateDestinatedStatus(
        locationTimezone,
        destinatedStatuses[2],
        'pending',
        orderStatusesDate[2]?.dateTime,
        orderStatusMessage?.ready_not_paid ??
          t('orderStatus.statusMessages.readyNotPaid'),
        currentStatus.name,
        true,
        orderStatusMessage?.additional_ready_not_paid ?? null,
      );

      return destinatedStatuses;
    }

    case OrderStatusName.CANCELLED: {
      updateDestinatedStatus(
        locationTimezone,
        destinatedStatuses[0],
        'completed',
        orderStatusesDate[0]?.dateTime,
      );

      updateDestinatedStatus(
        locationTimezone,
        destinatedStatuses[2],
        'cancelled',
        orderStatusesDate[2]?.dateTime,
        orderStatusMessage?.cancelled ??
          t('orderStatus.statusMessages.cancelled'),
        currentStatus.name,
        false,
        orderStatusMessage?.additional_cancelled ?? null,
      );

      return destinatedStatuses;
    }

    default:
      return destinatedStatuses;
  }
};
